import { Alert, Button, CardContent, CardHeader, CircularProgress, Stack, Typography } from "@mui/material"
import { useState } from "react"
import { OneCard } from "lib/@components/one-card"
import { Case, CaseElse, Switch } from "lib/@components/switch"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { ELEMENTSOFAQUICKWIN } from "plugins/toolbox/constants"
import { classifyQuickWin } from "plugins/toolbox/client/controller/quick-win-classifier.controller.js"
import { QuickWinTable } from "./quick-win-table"

const PANEL_IDLE = "idle"
const PANEL_RUNNING = "running"
const PANEL_EMPTY = "empty"
const PANEL_RESULTS = "results"

const NO_CONNECTION = "No or Tangential Connection"
const RESULT_LIMIT = 200

export function QuickWinPanel() {
    const [running, setRunning] = useState(false)
    const [results, setResults] = useState(null)
    return (
        <OneCard>
            <CardHeader
                title="Quick Wins"
                subheader="Scores every active FIQ ticket against the quick-win tags."
                action={
                    <Button variant="contained" onClick={runCheck} disabled={running}>
                        Find Quick Wins
                    </Button>
                }
            />
            <CardContent>
                <Stack spacing={3}>
                    <Switch value={panelState()}>
                        <Case when={PANEL_RUNNING}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                    Classifying FIQ tickets.
                                </Typography>
                            </Stack>
                        </Case>
                        <Case when={PANEL_EMPTY}>
                            <Alert severity="success">No quick-win candidates found.</Alert>
                        </Case>
                        <Case when={PANEL_RESULTS}>
                            <QuickWinTable results={results ?? []} />
                        </Case>
                        <CaseElse>
                            <Typography variant="body2" color="text.secondary">
                                Run the classifier to see which FIQ tickets look like quick wins.
                            </Typography>
                        </CaseElse>
                    </Switch>
                </Stack>
            </CardContent>
        </OneCard>
    )

    function panelState() {
        if (running) return PANEL_RUNNING
        if (!results) return PANEL_IDLE
        if (!results.length) return PANEL_EMPTY
        return PANEL_RESULTS
    }

    async function runCheck() {
        if (running) return

        setRunning(true)
        setResults(null)

        try {
            const found = await classifyQuickWin({ tags: ELEMENTSOFAQUICKWIN })
            setResults(toRows(found ?? []))
        } catch (error) {
            errorSnackbar(`Quick win classification failed: ${error?.message ?? "unknown error"}`)
        } finally {
            setRunning(false)
        }
    }

    function toRows(tickets) {
        return tickets
            .map(toRow)
            .filter((row) => row.matches.length)
            .sort((a, b) => b.bestCloseness - a.bestCloseness)
            .slice(0, RESULT_LIMIT)
    }

    function toRow(ticket) {
        const matches = (ticket.possibleCategories ?? [])
            .filter((category) => category.stats !== NO_CONNECTION)
            .map((category) => ({ ...category, closeness: Number(category.closeness) }))
        const bestCloseness = Math.max(0, ...matches.map((match) => match.closeness))
        return { key: ticket.key, summary: ticket.summary, status: ticket.status, matches, bestCloseness }
    }
}
